import { v4 as uuidv4 } from 'uuid';

import { flowchartLogger } from 'src/lib/logger';

import { callPropertyOptions } from '../components/CallPropertyInput';

import { defaultEntryNode } from './defaultData';

import type {
  FlowChartData,
  NodeSimple,
  CallflowNodeBase,
  DialplanDataBase,
  SortableInput,
  MappingRule,
  QueueInstruction,
  UserInstruction,
  ExtensionInstruction,
  FlowChartNodeData,
} from './types';
import type { CallPropertyOptions } from '../components/CallPropertyInput';
import type {
  CallflowAiAssistant,
  CallflowAiVoiceAnalysis,
  CallflowAnswer,
  CallflowCalendar,
  CallflowCallMe,
  CallflowConference,
  CallflowDisconnect,
  CallflowExtension,
  CallflowForward,
  CallflowGoto,
  CallflowGroup,
  CallflowIsExternal,
  CallflowIsFromQueue,
  CallflowHTTP,
  CallflowHunt,
  CallflowMapping,
  CallflowCustomRouter,
  CallflowPlayback,
  CallflowPresenceRouter,
  CallflowQueue,
  CallflowResponseRouter,
  CallflowRouter,
  CallflowUser,
  CallflowMappingCustomSetProperty,
  CallflowMappingCustomMatchProperty,
  CallflowMappingCallMatchProperty,
  CallflowPlaybackAudioSourceSoundfile,
  CallflowPlaybackAudioSourceUrl,
  CallflowIvr,
  CallflowDistributionList,
  CallflowLcr,
  CallflowPersonalVoicemail,
  CallflowSpeech,
  CallflowUserVoicemail,
} from '@ragu-components/ord-types';
import type { Node, Edge } from '@xyflow/react';

// Helper to check if instructions is an array (not a string for ai_assistant)
function isInstructionsArray(
  instructions: QueueInstruction[] | UserInstruction[] | ExtensionInstruction[] | string | undefined
): instructions is ExtensionInstruction[] | UserInstruction[] | QueueInstruction[] {
  return Array.isArray(instructions);
}

/** Thrown for any node-specific validation error, so callers can locate the offending node on the canvas. */
export class NodeValidationError extends Error {
  readonly nodeId: string;

  constructor(message: string, nodeId: string) {
    super(message);
    this.name = 'NodeValidationError';
    this.nodeId = nodeId;
  }
}

const createMappingProperty = (
  property: string
): CallflowMappingCustomSetProperty | CallflowMappingCustomMatchProperty | CallflowMappingCallMatchProperty => {
  if (callPropertyOptions.includes(property as CallPropertyOptions)) {
    return { location: 'call', property: property as CallPropertyOptions };
  } else {
    return { location: 'custom', property };
  }
};

export default class BackendData {
  private readonly nodes: Array<Node<FlowChartNodeData>>;
  private readonly edges: Edge[];

  constructor(data: FlowChartData) {
    this.nodes = data.nodes;
    this.edges = data.edges;
  }

  prepare(resource = 'callflows'): DialplanDataBase {
    flowchartLogger.debug('Preparing data for backend', this.nodes, this.edges);
    const nodes = [...this.nodes];
    if (nodes.length === 0) return this.getDefaultConfig(resource);

    // Remove the 'type=entry' node from the list of saved nodes.
    nodes.shift();

    const firstNodeId = this.edges.find((edge) => edge.source === 'entry')?.target;

    if (firstNodeId === undefined) {
      flowchartLogger.debug('Entry node connection not found, returning default config');
      return this.getDefaultConfig(resource);
    }

    // find and remove the first node from the list
    const firstNode = nodes.find((node) => node.id === firstNodeId);
    if (firstNode === undefined) {
      flowchartLogger.debug('First node not found, returning default config');
      return this.getDefaultConfig(resource);
    }
    nodes.splice(nodes.indexOf(firstNode), 1);

    const entryNode = this.convertNodeData(firstNode);
    if (typeof entryNode === 'string') return entryNode;

    const dialplanData: DialplanDataBase = {
      entry: entryNode,
      nodes: [],
    };

    for (const node of nodes) {
      const nodeData = this.convertNodeData(node);
      dialplanData.nodes.push(nodeData);
    }

    return dialplanData;
  }

  // Generate new uuids for nodes ( required on backup restore process )
  regenerateNodeIds(): void {
    // some nodes don't need to be regenerated
    const idsToSkip = ['entry'];

    // save pairs of old and new ids
    const idPairs: Record<string, string> = {};
    // generate new ids
    for (const node of this.nodes) {
      if (idsToSkip.includes(node.id)) continue;
      const newId = uuidv4();
      const oldId = node.id;
      idPairs[oldId] = newId;
      node.id = newId;
    }

    // update edges
    for (const edge of this.edges) {
      const newSource = idPairs[edge.source];
      if (newSource !== undefined) {
        edge.source = newSource;
      }
      const newTarget = idPairs[edge.target];
      if (newTarget !== undefined) {
        edge.target = newTarget;
      }
    }
  }

  getDefaultConfig(resource = 'callflow'): DialplanDataBase {
    return {
      entry: defaultEntryNode(resource),
      nodes: [],
    };
  }

  /** Every throw in `convertNodeData` goes through here, so every node error can be located on the canvas. */
  private nodeError(nodeId: string, message: string): NodeValidationError {
    return new NodeValidationError(message, nodeId);
  }

  convertNodeData(node: Node<FlowChartNodeData>): NodeSimple {
    flowchartLogger.debug('Converting node', node);

    if (this.getEdgeSource(node.id) === '') {
      flowchartLogger.error('Node has no source', node.type);
      throw this.nodeError(node.id, 'callflows.notifications.nodeIsNotConnectedToMainFlow');
    }

    const basicNode: CallflowNodeBase = {
      node_id: node.id,
      metadata: {
        ...(typeof node.data.metadata === 'object' && node.data.metadata !== null ? node.data.metadata : {}),
        ingord: {
          label: '',
          description: node.data.description ?? '',
          position: node.position,
          fixedX: node.data.fixedX,
          selected: node.selected,
        },
      },
    };

    if (node.type === 'router') {
      if (node.data.matchProperty === undefined || node.data.matchRules === undefined) {
        throw this.nodeError(node.id, 'Router node missing required properties');
      }
      const routerNode: CallflowRouter = {
        ...basicNode,
        type: 'router',
        match_property: node.data.matchProperty as CallPropertyOptions,
        match_rules: [],
      };

      node.data.matchRules.forEach((rule: SortableInput) => {
        // Skip empty rules
        if (rule.value === '') {
          return;
        }
        const nextId = this.getEdgeTarget(node.id, rule.id);
        if (nextId === '') {
          flowchartLogger.error(`Router rule has no target: ${rule.value}`);
          throw this.nodeError(node.id, 'callflows.nodes.router.matchRuleShouldHaveConnection');
        }
        routerNode.match_rules.push({
          connection: {
            next_id: nextId,
          },
          match_rule: rule.value,
        });
      });

      if (!callPropertyOptions.includes(node.data.matchProperty as CallPropertyOptions)) {
        const customRouterNode: CallflowCustomRouter = {
          ...routerNode,
          type: 'custom_router',
        };
        return customRouterNode;
      }

      return routerNode;
    } else if (node.type === 'mapping') {
      const nextId = this.getEdgeTarget(node.id);
      if (nextId === '') {
        flowchartLogger.error('Mapping has no target');
        throw this.nodeError(node.id, 'callflows.nodes.mapping.mappingHasNoTarget');
      }

      const mappingNode: CallflowMapping = {
        ...basicNode,
        type: 'mapping',
        connection: {
          next_id: nextId,
        },
        match_property: createMappingProperty(node.data.matchProperty as CallPropertyOptions),
        set_property: createMappingProperty(node.data.setProperty as CallPropertyOptions),
        match_rules: [],
      };
      if (
        node.data.mappingRules !== undefined &&
        node.data.mappingRules !== null &&
        node.data.mappingRules.length > 0
      ) {
        const firstRule = node.data.mappingRules[0];
        // Disallow empty mapping rules
        if (firstRule !== undefined && (firstRule.matchRule === '' || firstRule.replaceRule === '')) {
          flowchartLogger.error('Mapping rules are empty');
          throw this.nodeError(node.id, 'callflows.nodes.mapping.mappingRuleErrorEmpty');
        }
        node.data.mappingRules.forEach((rule: MappingRule) => {
          mappingNode.match_rules.push({
            match_rule: rule.matchRule,
            replace_rule: rule.replaceRule,
          });
        });
      }
      return mappingNode;
    } else if (node.type === 'calendar') {
      if (node.data.calendar === undefined || node.data.calendar === null) {
        flowchartLogger.error('Calendar is not selected');
        throw this.nodeError(node.id, 'callflows.nodes.calendar.calendarIsNotSelected');
      }
      const nextIdTrue = this.getEdgeTarget(node.id, 'true_connection');
      const nextIdFalse = this.getEdgeTarget(node.id, 'false_connection');
      if (nextIdTrue === '' || nextIdFalse === '') {
        flowchartLogger.error('Calendar has no target', nextIdFalse, nextIdTrue);
        throw this.nodeError(node.id, 'callflows.nodes.calendar.calendarHasNoTarget');
      }
      const calendarNode: CallflowCalendar = {
        ...basicNode,
        type: 'calendar',
        calendar: node.data.calendar.id,
        false_connection: {
          next_id: nextIdFalse,
        },
        true_connection: {
          next_id: nextIdTrue,
        },
      };
      return calendarNode;
    } else if (node.type === 'call_me') {
      const callMeNode: CallflowCallMe = {
        ...basicNode,
        type: 'call_me',
      };
      const nextId = this.getEdgeTarget(node.id);
      if (nextId !== '') {
        callMeNode.connection = {
          next_id: nextId,
        };
      }
      return callMeNode;
    } else if (node.type === 'extension') {
      const extensionNode: CallflowExtension = {
        ...basicNode,
        type: 'extension',
      };
      const nextId = this.getEdgeTarget(node.id, 'default_connection-source');
      if (nextId !== '') {
        extensionNode.default_connection = {
          next_id: nextId,
        };
      }
      if (
        node.data.instructions !== undefined &&
        isInstructionsArray(node.data.instructions) &&
        (node.data.instructions.length > 1 ||
          (node.data.instructions.length === 1 &&
            node.data.instructions.find((instruction) => instruction.match_exit_causes.length > 0) !== undefined))
      ) {
        extensionNode.connections = [];
        node.data.instructions.forEach((instruction) => {
          // Skip empty rules
          if (instruction.match_exit_causes.length === 0) {
            flowchartLogger.error(`Extension connection has no match_exit_causes: ${instruction.description ?? ''}`);
            throw this.nodeError(node.id, 'callflows.nodes.extension.connectionShouldHaveMatchExitCause');
          }
          const nextId = this.getEdgeTarget(node.id, String(instruction.id));
          if (nextId === '') {
            flowchartLogger.error(`Extension connection has no target: ${instruction.description ?? ''}`);
            throw this.nodeError(node.id, 'callflows.nodes.extension.connectionShouldHaveTarget');
          }
          if (extensionNode.connections !== undefined) {
            extensionNode.connections.push({
              connection: {
                next_id: nextId,
              },
              description: instruction.description ?? null,
              match_exit_causes: instruction.match_exit_causes as ExtensionInstruction['match_exit_causes'],
            });
          }
        });
      }
      return extensionNode;
    } else if (node.type === 'forward') {
      if (node.data.number === undefined || node.data.number === '') {
        flowchartLogger.error('Forward number is empty');
        throw this.nodeError(node.id, 'callflows.nodes.forward.forwardErrorEmpty');
      }
      const forwardNode: CallflowForward = {
        ...basicNode,
        type: 'forward',
        number: node.data.number,
      };
      return forwardNode;
    } else if (node.type === 'goto') {
      if (node.data.goto === undefined || node.data.goto === null) {
        flowchartLogger.error('Goto node is empty');
        throw this.nodeError(node.id, 'callflows.nodes.goto.gotoErrorEmpty');
      }
      const gotoNode: CallflowGoto = {
        ...basicNode,
        type: 'goto',
        callflow: node.data.goto.id,
      };
      return gotoNode;
    } else if (node.type === 'groupNode') {
      if (node.data.group === undefined || node.data.group === null) {
        flowchartLogger.error('Group node is empty');
        throw this.nodeError(node.id, 'callflows.nodes.groupNode.groupErrorEmpty');
      }
      const groupNode: CallflowGroup = {
        ...basicNode,
        type: 'group',
        group: node.data.group.id,
        call_timeout: node.data.call_timeout ?? 30,
      };

      const nextId = this.getEdgeTarget(node.id, 'default_connection-source');
      if (nextId !== '') {
        groupNode.default_connection = {
          next_id: nextId,
        };
      }
      if (
        node.data.instructions !== undefined &&
        isInstructionsArray(node.data.instructions) &&
        (node.data.instructions.length > 1 ||
          (node.data.instructions.length === 1 &&
            node.data.instructions.find((instruction) => instruction.match_exit_causes.length > 0) !== undefined))
      ) {
        groupNode.connections = [];
        node.data.instructions.forEach((instruction) => {
          // Skip empty rules
          if (instruction.match_exit_causes.length === 0) {
            flowchartLogger.error(`Extension connection has no match_exit_causes: ${instruction.description ?? ''}`);
            throw this.nodeError(node.id, 'callflows.nodes.extension.connectionShouldHaveMatchExitCause');
          }
          const nextId = this.getEdgeTarget(node.id, String(instruction.id));
          if (nextId === '') {
            flowchartLogger.error(`Extension connection has no target: ${instruction.description ?? ''}`);
            throw this.nodeError(node.id, 'callflows.nodes.extension.connectionShouldHaveTarget');
          }
          if (groupNode.connections !== undefined) {
            groupNode.connections.push({
              connection: {
                next_id: nextId,
              },
              description: instruction.description ?? null,
              match_exit_causes: instruction.match_exit_causes as ExtensionInstruction['match_exit_causes'],
            });
          }
        });
      }
      return groupNode;
    } else if (node.type === 'http') {
      const httpNode: CallflowHTTP = {
        ...basicNode,
        type: 'http',
        url: node.data.url ?? '',
        ssl_verify: node.data.sslVerify ?? true,
        headers: node.data.headers ?? [],
        method: node.data.method ?? 'get',
      };
      return httpNode;
    } else if (node.type === 'hunt') {
      if (
        node.data.huntingGroupId === undefined ||
        node.data.huntingGroupId === null ||
        node.data.huntingGroupId === ''
      ) {
        flowchartLogger.error('Hunt group is empty');
        throw this.nodeError(node.id, 'callflows.nodes.hunt.huntingGroupErrorEmpty');
      }
      const huntNode: CallflowHunt = {
        ...basicNode,
        type: 'hunt',
        enable_screening: node.data.enableScreening ?? false,
        parallel: node.data.parallel ?? false,
        hunting_group: String(node.data.huntingGroupId ?? ''),
      };
      const nextId = this.getEdgeTarget(node.id);
      if (nextId !== '') {
        huntNode.connection = {
          next_id: nextId,
        };
      }
      return huntNode;
    } else if (node.type === 'ivr') {
      if (
        node.data.errorSoundfile === undefined ||
        node.data.errorSoundfile === null ||
        node.data.menuSoundfile === undefined ||
        node.data.menuSoundfile === null ||
        node.data.timeoutSoundfile === undefined ||
        node.data.timeoutSoundfile === null
      ) {
        flowchartLogger.error('IVR soundfile is empty');
        throw this.nodeError(node.id, 'callflows.nodes.ivr.soundfileErrorEmpty');
      }

      const ivrNode: CallflowIvr = {
        ...basicNode,
        type: 'ivr',
        error_soundfile: {
          soundfile: node.data.errorSoundfile.id,
        },
        menu_soundfile: {
          soundfile: node.data.menuSoundfile.id,
        },
        timeout_soundfile: {
          soundfile: node.data.timeoutSoundfile.id,
        },
        retries: Number(node.data.retries ?? 0),
        timeout: Number(node.data.timeout ?? 5000),
        selections: [],
      };
      if (node.data.selections === undefined) {
        flowchartLogger.error('IVR selections are empty');
        throw this.nodeError(node.id, 'callflows.nodes.ivr.selectionsErrorEmpty');
      }
      node.data.selections.forEach((selection: SortableInput) => {
        // Skip empty rules
        if (selection.value === '') {
          return;
        }
        const nextId = this.getEdgeTarget(node.id, selection.id);
        if (nextId === '') {
          flowchartLogger.error(`IVR selection has no target: ${selection.value}`);
          throw this.nodeError(node.id, 'callflows.nodes.ivr.selectionShouldHaveConnection');
        }
        ivrNode.selections.push({
          connection: {
            next_id: nextId,
          },
          selection: selection.value,
        });
      });

      return ivrNode;
    } else if (node.type === 'is_from_queue') {
      const nextIdTrue = this.getEdgeTarget(node.id, 'true_connection');
      const nextIdFalse = this.getEdgeTarget(node.id, 'false_connection');
      if (nextIdTrue === '' || nextIdFalse === '') {
        flowchartLogger.error('Is from queue has no target', nextIdFalse, nextIdTrue);
        throw this.nodeError(node.id, 'callflows.nodes.is_from_queue.isFromQueueHasNoTarget');
      }
      const isFromQueueNode: CallflowIsFromQueue = {
        ...basicNode,
        type: 'is_from_queue',
        false_connection: {
          next_id: nextIdFalse,
        },
        true_connection: {
          next_id: nextIdTrue,
        },
      };
      return isFromQueueNode;
    } else if (node.type === 'is_external') {
      const nextIdTrue = this.getEdgeTarget(node.id, 'true_connection');
      const nextIdFalse = this.getEdgeTarget(node.id, 'false_connection');
      if (nextIdTrue === '' || nextIdFalse === '') {
        flowchartLogger.error('Is external has no target', nextIdFalse, nextIdTrue);
        throw this.nodeError(node.id, 'callflows.nodes.is_external.isExternalHasNoTarget');
      }
      const isExternalNode: CallflowIsExternal = {
        ...basicNode,
        type: 'is_external',
        false_connection: {
          next_id: nextIdFalse,
        },
        true_connection: {
          next_id: nextIdTrue,
        },
      };
      return isExternalNode;
    } else if (node.type === 'user') {
      if (node.data.user === undefined || node.data.user === null) {
        flowchartLogger.error('User node is empty', node);
        throw this.nodeError(node.id, 'callflows.nodes.user.userIsNotSelected');
      }
      const userNode: CallflowUser = {
        ...basicNode,
        type: 'user',
        user: node.data.user.id,
      };
      const nextId = this.getEdgeTarget(node.id, 'default_connection-source');
      if (nextId !== '') {
        userNode.default_connection = {
          next_id: nextId,
        };
      }
      if (
        node.data.instructions !== undefined &&
        isInstructionsArray(node.data.instructions) &&
        (node.data.instructions.length > 1 ||
          (node.data.instructions.length === 1 &&
            node.data.instructions.find((instruction) => instruction.match_exit_causes.length > 0) !== undefined))
      ) {
        userNode.connections = [];

        node.data.instructions.forEach((instruction) => {
          // Skip empty rules
          if (instruction.match_exit_causes.length === 0) {
            flowchartLogger.error(`User connection has no match_exit_causes: ${instruction.description ?? ''}`);
            throw this.nodeError(node.id, 'callflows.nodes.user.connectionShouldHaveMatchExitCause');
          }
          const nextId = this.getEdgeTarget(node.id, String(instruction.id));
          if (nextId === '') {
            flowchartLogger.error(`User connection has no target: ${instruction.description ?? ''}`);
            throw this.nodeError(node.id, 'callflows.nodes.user.connectionShouldHaveTarget');
          }
          if (userNode.connections !== undefined) {
            userNode.connections.push({
              connection: {
                next_id: nextId,
              },
              description: instruction.description ?? null,
              match_exit_causes: instruction.match_exit_causes as UserInstruction['match_exit_causes'],
            });
          }
        });
      }
      return userNode;
    } else if (node.type === 'lcr') {
      if (node.data.user === undefined || node.data.user === null) {
        flowchartLogger.error('LCR node is empty', node);
        throw this.nodeError(node.id, 'callflows.nodes.lcr.userIsNotSelected');
      }
      const lcrNode: CallflowLcr = {
        ...basicNode,
        type: 'lcr',
        user: node.data.user.id,
      };
      return lcrNode;
    } else if (node.type === 'playback') {
      if (node.data.audioSource === undefined && node.data.url === undefined) {
        flowchartLogger.error('Playback node is empty');
        throw this.nodeError(node.id, 'callflows.nodes.playback.audioSourceNotSelected');
      }
      let audioSource: CallflowPlaybackAudioSourceSoundfile | CallflowPlaybackAudioSourceUrl;
      if (node.data.audioSource !== null && node.data.audioSource !== undefined) {
        audioSource = {
          soundfile: node.data.audioSource.id,
          type: 'soundfile',
        };
      } else {
        if (node.data.url === undefined || node.data.url === null || node.data.url === '') {
          flowchartLogger.error('Playback node has no URL for audio source');
          throw this.nodeError(node.id, 'callflows.nodes.playback.audioSourceNotSelected');
        }
        audioSource = {
          url: node.data.url,
          type: 'url',
          method: node.data.method ?? 'get',
          ssl_verify: node.data.sslVerify ?? true,
          headers: node.data.headers ?? [],
        };
      }
      const playbackNode: CallflowPlayback = {
        ...basicNode,
        type: 'playback',
        audio_source: audioSource,
      };
      if (
        node.data.outputPropName !== null &&
        node.data.outputPropName !== undefined &&
        node.data.outputPropName !== ''
      ) {
        playbackNode.dtmf_gather = {
          max_digits: node.data.maxDigits,
          output_prop_name: node.data.outputPropName,
          terminators: node.data.terminators,
          timeout: Number(node.data.timeout ?? 5000),
        };
      }
      // TODO extract connection setter and share it with other nodes
      const nextId = this.getEdgeTarget(node.id);
      if (nextId !== '') {
        playbackNode.connection = {
          next_id: nextId,
        };
      }
      return playbackNode;
    } else if (node.type === 'presence_router') {
      const nextIdAvailable = this.getEdgeTarget(node.id, 'available_connection');
      const nextIdAway = this.getEdgeTarget(node.id, 'away_connection');
      const nextIdBusy = this.getEdgeTarget(node.id, 'busy_connection');
      const nextIdUnreachable = this.getEdgeTarget(node.id, 'unreachable_connection');
      if (nextIdAvailable === '' || nextIdAway === '' || nextIdBusy === '' || nextIdUnreachable === '') {
        flowchartLogger.error('Presence router node has no connection');
        throw this.nodeError(node.id, 'callflows.nodes.presence_router.presenceRouterShouldHaveConnection');
      }
      const presenceRouterNode: CallflowPresenceRouter = {
        ...basicNode,
        available_connection: {
          next_id: nextIdAvailable,
        },
        away_connection: {
          next_id: nextIdAway,
        },
        busy_connection: {
          next_id: nextIdBusy,
        },
        unreachable_connection: {
          next_id: nextIdUnreachable,
        },
        type: 'presence_router',
      };
      return presenceRouterNode;
    } else if (node.type === 'queue') {
      if (node.data.queue === undefined || node.data.queue === null) {
        flowchartLogger.error('Queue node is empty');
        throw this.nodeError(node.id, 'callflows.nodes.queue.queueErrorEmpty');
      }
      const queueNode: CallflowQueue = {
        ...basicNode,
        type: 'queue',
        queue: node.data.queue.id,
      };
      const nextId = this.getEdgeTarget(node.id, 'default_connection-source');
      if (nextId !== '') {
        queueNode.default_connection = {
          next_id: nextId,
        };
      }
      if (
        node.data.instructions !== undefined &&
        isInstructionsArray(node.data.instructions) &&
        (node.data.instructions.length > 1 ||
          (node.data.instructions.length === 1 &&
            node.data.instructions.find((instruction) => instruction.match_exit_causes.length > 0) !== undefined))
      ) {
        queueNode.connections = [];
        node.data.instructions.forEach((instruction) => {
          // Skip empty rules
          if (instruction.match_exit_causes.length === 0) {
            flowchartLogger.error(`Queue connection has no match_exit_causes: ${instruction.description ?? ''}`);
            throw this.nodeError(node.id, 'callflows.nodes.queue.connectionShouldHaveMatchExitCause');
          }
          const nextId = this.getEdgeTarget(node.id, String(instruction.id));
          if (nextId === '') {
            flowchartLogger.error(`Queue connection has no target: ${instruction.description ?? ''}`);
            throw this.nodeError(node.id, 'callflows.nodes.queue.connectionShouldHaveTarget');
          }
          if (queueNode.connections !== undefined) {
            queueNode.connections.push({
              connection: {
                next_id: nextId,
              },
              description: instruction.description ?? null,
              match_exit_causes: instruction.match_exit_causes as QueueInstruction['match_exit_causes'],
            });
          }
        });
      }
      return queueNode;
    } else if (node.type === 'response_router') {
      const nextIdBusy = this.getEdgeTarget(node.id, 'busy_connection');
      const nextIdTimeout = this.getEdgeTarget(node.id, 'timeout_connection');
      const nextIdUnreachable = this.getEdgeTarget(node.id, 'unreachable_connection');
      if (nextIdBusy === '' || nextIdTimeout === '' || nextIdUnreachable === '') {
        flowchartLogger.error('Response router node has no connection');
        throw this.nodeError(node.id, 'callflows.nodes.response_router.responseRouterShouldHaveConnection');
      }
      const responseRouterNode: CallflowResponseRouter = {
        ...basicNode,
        busy_connection: {
          next_id: nextIdBusy,
        },
        timeout_connection: {
          next_id: nextIdTimeout,
        },
        unreachable_connection: {
          next_id: nextIdUnreachable,
        },
        type: 'response_router',
      };
      return responseRouterNode;
    } else if (node.type === 'speech') {
      if (node.data.auth_key === undefined || node.data.auth_key === '') {
        flowchartLogger.error('Speech auth key is empty');
        throw this.nodeError(node.id, 'callflows.nodes.speech.authKeyErrorEmpty');
      }
      if (node.data.text === undefined || node.data.text === '') {
        flowchartLogger.error('Speech text is empty');
        throw this.nodeError(node.id, 'callflows.nodes.speech.textErrorEmpty');
      }
      const encoding = node.data.encoding ?? 'wav';
      const language = node.data.language ?? 'it';
      const speechNode: CallflowSpeech = {
        ...basicNode,
        auth_key: (node.data.auth_key as string) ?? '',
        text: node.data.text ?? '',
        encoding: encoding === 'mp3' || encoding === 'wav' ? encoding : 'wav',
        language: language === 'it' || language === 'en' ? language : 'it',
        type: 'speech',
      };
      const nextId = this.getEdgeTarget(node.id);
      if (nextId !== '') {
        speechNode.connection = {
          next_id: nextId,
        };
      }
      return speechNode;
    } else if (node.type === 'ai_assistant') {
      if (node.data.instructions === undefined || node.data.instructions === '') {
        flowchartLogger.error('AI Assistant instructions are empty');
        throw this.nodeError(node.id, 'callflows.nodes.ai_assistant.instructionsErrorEmpty');
      }

      const aiAssistantNode: CallflowAiAssistant = {
        ...basicNode,
        type: 'ai_assistant',
        instructions: node.data.instructions as string,
        model: node.data.model !== undefined ? (node.data.model as CallflowAiAssistant['model']) : 'gpt-realtime',
        voice: node.data.voice !== undefined ? (node.data.voice as CallflowAiAssistant['voice']) : 'marin',
      };

      if (node.data.transcription !== undefined) {
        aiAssistantNode.transcription = node.data.transcription as CallflowAiAssistant['transcription'];
      }

      return aiAssistantNode;
    } else if (node.type === 'ai_voice_analysis') {
      const nextId = this.getEdgeTarget(node.id);
      if (nextId === '') {
        flowchartLogger.error('AI voice analysis node has no connection');
        throw new Error('callflows.nodes.ai_voice_analysis.aiVoiceAnalysisShouldHaveConnection');
      }
      const aiVoiceAnalysisNode: CallflowAiVoiceAnalysis = {
        ...basicNode,
        type: 'ai_voice_analysis',
        connection: {
          next_id: nextId,
        },
        action: node.data.action === 'stop' ? 'stop' : 'start',
      };
      return aiVoiceAnalysisNode;
    } else if (node.type === 'answer') {
      const nextId = this.getEdgeTarget(node.id);
      if (nextId === '') {
        flowchartLogger.error('Answer node has no connection');
        throw this.nodeError(node.id, 'callflows.nodes.answer.answerShouldHaveConnection');
      }
      const answerNode: CallflowAnswer = {
        ...basicNode,
        connection: {
          next_id: nextId,
        },
        type: 'answer',
        delay: node.data.delay ?? 0,
      };
      return answerNode;
    } else if (node.type === 'conference') {
      if (node.data.conference === undefined || node.data.conference === null) {
        flowchartLogger.error('Conference node is empty');
        throw this.nodeError(node.id, 'callflows.nodes.conference.conferenceErrorEmpty');
      }
      const conferenceNode: CallflowConference = {
        ...basicNode,
        type: 'conference',
        conference: node.data.conference.id,
      };
      return conferenceNode;
    } else if (node.type === 'disconnect') {
      const reason = node.data.reason ?? 'normal';
      const disconnectNode: CallflowDisconnect = {
        ...basicNode,
        type: 'disconnect',
        reason: reason === 'normal' || reason === 'not_found' ? reason : 'normal',
      };
      return disconnectNode;
    } else if (node.type === 'distribution_list') {
      if (node.data.distribution_list === undefined || node.data.distribution_list === null) {
        flowchartLogger.error('Distribution list node is empty');
        throw this.nodeError(node.id, 'callflows.nodes.distribution_list.distributionListErrorEmpty');
      }
      const nextIdTrue = this.getEdgeTarget(node.id, 'true_connection');
      const nextIdFalse = this.getEdgeTarget(node.id, 'false_connection');
      if (nextIdTrue === '' || nextIdFalse === '') {
        flowchartLogger.error('Distribution list has no target', nextIdFalse, nextIdTrue);
        throw this.nodeError(node.id, 'callflows.nodes.distribution_list.distributionListHasNoTarget');
      }
      const distributionListNode: CallflowDistributionList = {
        ...basicNode,
        type: 'distribution_list',
        distribution_list: node.data.distribution_list.id,
        true_connection: {
          next_id: nextIdTrue,
        },
        false_connection: {
          next_id: nextIdFalse,
        },
      };
      return distributionListNode;
    } else if (node.type === 'personal_voicemail') {
      const personalVoicemailNode: CallflowPersonalVoicemail = {
        ...basicNode,
        type: 'personal_voicemail',
      };
      return personalVoicemailNode;
    } else if (node.type === 'user_voicemail') {
      if (node.data.user === undefined || node.data.user === null) {
        flowchartLogger.error('User voicemail node is empty', node);
        throw this.nodeError(node.id, 'callflows.nodes.user_voicemail.userIsNotSelected');
      }
      const userVoicemailNode: CallflowUserVoicemail = {
        ...basicNode,
        type: 'user_voicemail',
        user: node.data.user.id,
      };
      return userVoicemailNode;
    }
    flowchartLogger.error('Unknown node type', node.type);
    throw this.nodeError(node.id, 'callflows.notifications.errorSaving');
  }

  getEdgeTarget(source: string, sourceHandle = ''): string {
    for (const edge of this.edges) {
      if (edge.source === source && (sourceHandle === '' || edge.sourceHandle === sourceHandle)) {
        return edge.target;
      }
    }
    return '';
  }

  getEdgeSource(target: string, targetHandle = ''): string {
    for (const edge of this.edges) {
      if (edge.target === target && (targetHandle === '' || edge.targetHandle === targetHandle)) {
        return edge.source;
      }
    }
    return '';
  }
}
